import { MutationTuple, useMutation } from '@apollo/client';

import {
  evictCache,
  GET_COMPANY_OFFICER_PAYROLL_QRES,
  GetCompanyOfficerPayrollQresQuery,
  GetCompanyOfficerPayrollQresQueryVariables,
  UPDATE_COMPANY_OFFICER,
  UpdateCompanyOfficerMutation,
  UpdateCompanyOfficerMutationVariables,
} from '@neo.tax/shared';

export const useUpdateCompanyOfficer = (
  fiscalYearId: string,
): MutationTuple<UpdateCompanyOfficerMutation, UpdateCompanyOfficerMutationVariables> =>
  useMutation<UpdateCompanyOfficerMutation, UpdateCompanyOfficerMutationVariables>(UPDATE_COMPANY_OFFICER, {
    context: {
      errorToast: {
        title: 'Error updating company officers',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },

    update: (cache, { data }) => {
      // Evict cache for related queries to avoid stale data now that we've updated the company officer
      evictCache(cache, ['getPayrollQres']);
      evictCache(cache, ['searchForPotentialCompanyOfficers']);

      // Update GetCompanyOfficerPayrollQres query manually instead of refetching to avoid janky UI for components like AutocompleteTagInput
      const mutatedCompanyOfficer = data?.updateCompanyOfficer;
      if (mutatedCompanyOfficer) {
        cache.updateQuery<GetCompanyOfficerPayrollQresQuery, GetCompanyOfficerPayrollQresQueryVariables>(
          {
            query: GET_COMPANY_OFFICER_PAYROLL_QRES,
            variables: {
              fiscalYearId,
            },
          },
          (data) => {
            if (!data) {
              return { getCompanyOfficerPayrollQres: [mutatedCompanyOfficer] };
            }

            const getCompanyOfficerPayrollQres = mutatedCompanyOfficer.isOfficer
              ? [...data.getCompanyOfficerPayrollQres, mutatedCompanyOfficer].sort((a, b) =>
                  a.name.localeCompare(b.name),
                )
              : data.getCompanyOfficerPayrollQres.filter((officer) => officer.id !== mutatedCompanyOfficer.id);
            return {
              getCompanyOfficerPayrollQres,
            };
          },
        );
      }
    },
  });
